import { useRef } from "react";
import useSWR, { Key, SWRConfiguration, SWRResponse } from "swr";
import useSWRMutation, { SWRMutationConfiguration, SWRMutationResponse } from "swr/mutation";
import { useGlobalErrorHandler } from "~/hooks/useGlobalErrorHandler";
import { apiClient as fetcher } from "~/utils/apiClient";
import { safelyCamelize, unwrapData } from "~/utils/object";

const noopTransform = (data: any) => data;
interface ExtraFetcherOptions<ResponseType = any> {
  // Specify "data" to only return data prop from response, or true for the whole response
  shouldCamelize?: true | "data";
  skipCamelizeKeys?: string[];
  transform?: (data: ResponseType) => ResponseType;
  withGlobalErrorHandler?: true;
  requestInitOptions?: RequestInit;
}
export const camelizingFetcher =
  <ResponseType>(
    {
      getNestedDataOnly = false,
      skipCamelizeKeys,
      transform = noopTransform,
    }: {
      getNestedDataOnly?: boolean;
    } & Pick<ExtraFetcherOptions<ResponseType>, "skipCamelizeKeys" | "transform">,
    options?: RequestInit
  ) =>
  async (url: string) => {
    const json = await fetcher<ResponseType>(url, options);
    // Always fall back to full response if json.data is empty
    const data = getNestedDataOnly ? unwrapData(json) : json;
    // Note: skipCamelizeKeys is still useful here since it allows skipping *nested* objects
    return transform(safelyCamelize(data, false, skipCamelizeKeys));
  };

export const useFetcher = <ResponseType>(
  key: Key,
  options: SWRConfiguration & ExtraFetcherOptions = {}
): SWRResponse<ResponseType> => {
  const globalErrorHandler = useGlobalErrorHandler();
  const {
    shouldCamelize,
    skipCamelizeKeys,
    withGlobalErrorHandler,
    transform = noopTransform,
    requestInitOptions,
    ...swrOptions
  } = options;
  if (withGlobalErrorHandler) {
    swrOptions.onError = globalErrorHandler;
  }

  const fetchFn = shouldCamelize
    ? camelizingFetcher<ResponseType>(
        {
          getNestedDataOnly: shouldCamelize === "data",
          skipCamelizeKeys,
          transform,
        },
        requestInitOptions
      )
    : (url: string) => fetcher<ResponseType>(url, requestInitOptions);

  return useSWR(key, fetchFn, swrOptions);
};

export const useStickyFetcher = <ResponseType>(
  key: Key,
  options: SWRConfiguration = {}
): SWRResponse<ResponseType> => {
  const val = useRef<ResponseType>(undefined);
  const { data, ...rest } = useFetcher<ResponseType>(key, options);
  if (data !== undefined) {
    val.current = data;
  }
  return {
    ...rest,
    data: val.current,
  };
};

export interface ExtraMutatorProps extends ExtraFetcherOptions {
  requestInit?: RequestInit;
}
const getMutator =
  <MutatorArg = unknown>(fetcherProps: ExtraMutatorProps) =>
  async (key: Key, { arg }: { arg: MutatorArg }) => {
    const [path] = Array.isArray(key) ? key : [key];
    const {
      requestInit = {},
      shouldCamelize,
      skipCamelizeKeys,
      transform = noopTransform,
    } = fetcherProps;

    if (!requestInit.method) {
      requestInit.method = "PATCH";
    }
    if (arg instanceof FormData) {
      requestInit.body = arg;
    } else if (typeof arg === "object") {
      requestInit.headers = {
        "Content-Type": "application/json",
        ...(requestInit.headers || {}),
      };
      requestInit.body = JSON.stringify(arg);
    }
    return shouldCamelize
      ? camelizingFetcher(
          {
            getNestedDataOnly: shouldCamelize === "data",
            skipCamelizeKeys,
            transform,
          },
          requestInit
        )(path)
      : fetcher(path, requestInit);
  };

export const useMutator = <Data = never, ExtraArg = never, Error = unknown>(
  key: Key,
  options: SWRMutationConfiguration<Data, Error> & ExtraMutatorProps = {}
): SWRMutationResponse<Data, Error, Key, ExtraArg> => {
  const globalErrorHandler = useGlobalErrorHandler();
  const { requestInit, shouldCamelize, withGlobalErrorHandler, ...swrOptions } = options;
  if (withGlobalErrorHandler) {
    swrOptions.onError = globalErrorHandler;
  }

  return useSWRMutation<Data, Error, Key, ExtraArg>(
    key,
    getMutator<ExtraArg>({
      requestInit,
      shouldCamelize,
    }),
    swrOptions
  );
};
